import { z } from 'zod'
import { leadStatsMetricKindSchema, leadStatsNumberKindSchema } from '../enums/lead-stats-metric-kind.enum.js'
import { leadStatsViewSchema } from '../enums/lead-stats-view.enum.js'
import { calendarDaySchema, instantSchema } from '../validators/temporal.validator.js'
import { adPeriodWindowDtoSchema } from './ad-numbers.dto.js'

/**
 * A venue's lead stats as its dashboard shows them (docs/gohighlevel-stats.spec.md,
 * "The Leads panel"): one pipeline, one view, one period, with each number's
 * change, trend and cost, worked out by the API from the leads it keeps.
 */

/** One period's value in a trend. */
export const leadStatsTrendPointDtoSchema = z.object({
  start: calendarDaySchema,
  value: z.number().nullable(),
  stillMoving: z.boolean(),
})
export type LeadStatsTrendPointDto = z.infer<typeof leadStatsTrendPointDtoSchema>

/** A number for the period chosen. Null values mean "not counted": see `countedFrom`. */
export const leadStatsNumberDtoSchema = z.object({
  metricId: z.string().uuid(),
  name: z.string(),
  kind: leadStatsNumberKindSchema,
  /** Whether it depends on what happens after a lead comes in, so it keeps rising. */
  moves: z.boolean(),
  value: z.number().int().nonnegative().nullable(),
  /** The period before, as it stands now or, when `sameAge`, as it stood at the same age. */
  previous: z.number().int().nonnegative().nullable(),
  sameAge: z.boolean(),
  stillMoving: z.boolean(),
  /**
   * The first day counted, when the period began before it: in the "what
   * happened" view, the day recording began; for a number counted from
   * calendars, in either view, the day of its calendars' first appointment.
   */
  countedFrom: calendarDaySchema.nullable(),
  /** The last 12 periods, oldest first, ending with this one. */
  trend: z.array(leadStatsTrendPointDtoSchema),
  /** The venue's ad spend in the period over the value, in micros of `spend.currencyCode`. */
  costPerMicros: z.number().int().nonnegative().nullable(),
})
export type LeadStatsNumberDto = z.infer<typeof leadStatsNumberDtoSchema>

/** A conversion for the period chosen, as a fraction (0.143 is 14.3%). */
export const leadStatsConversionDtoSchema = z.object({
  metricId: z.string().uuid(),
  name: z.string(),
  value: z.number().nonnegative().nullable(),
  previous: z.number().nonnegative().nullable(),
  sameAge: z.boolean(),
  stillMoving: z.boolean(),
  trend: z.array(leadStatsTrendPointDtoSchema),
})
export type LeadStatsConversionDto = z.infer<typeof leadStatsConversionDtoSchema>

/** One of the venue's connections, as far as its numbers go. */
export const leadStatsConnectionDtoSchema = z.object({
  id: z.string().uuid(),
  locationName: z.string(),
  readAt: instantSchema.nullable(),
  failingSince: instantSchema.nullable(),
  calendarsReadable: z.boolean().nullable(),
  reading: z.boolean(),
})

export const venueLeadStatsDtoSchema = z.object({
  venueId: z.string().uuid(),
  venueName: z.string(),
  timeZone: z.string(),
  connections: z.array(leadStatsConnectionDtoSchema),
  /** The oldest complete read among working connections. */
  updatedAt: instantSchema.nullable(),
  /** The newest read is more than two days old. */
  stale: z.boolean(),
  /** A read of the venue is under way. */
  reading: z.boolean(),
  /** A connection's first read hasn't finished, so the numbers aren't shown yet. */
  firstReadPending: z.boolean(),
  /** The first day every connection was recording: what "what happened" counts from. */
  recordingSince: calendarDaySchema.nullable(),
  pipelines: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  pipelineId: z.string().uuid().nullable(),
  view: leadStatsViewSchema,
  /** Every period there are numbers for, newest first. */
  periods: z.array(z.object({ start: calendarDaySchema, inProgress: z.boolean() })),
  /** The period shown, and what it is compared with. Null when there is none. */
  window: adPeriodWindowDtoSchema.nullable(),
  numbers: z.array(leadStatsNumberDtoSchema),
  conversions: z.array(leadStatsConversionDtoSchema),
  /** The period's leads by source, largest first, the rest as one "Other" (name null). */
  sources: z.array(z.object({ name: z.string().nullable(), count: z.number().int().nonnegative() })),
  /** The venue's ad spend in the period, when the company has ads and there was any. */
  spend: z.object({ spendMicros: z.number().int().nonnegative(), currencyCode: z.string() }).nullable(),
  /** At a venue with more than one connection, each one's numbers on their own. */
  split: z
    .array(
      z.object({
        connectionId: z.string().uuid(),
        locationName: z.string(),
        values: z.array(z.object({ metricId: z.string().uuid(), value: z.number().int().nonnegative().nullable() })),
      }),
    )
    .nullable(),
  /** Whether this person can read the venue's GoHighLevel now. */
  canRefresh: z.boolean(),
})
export type VenueLeadStatsDto = z.infer<typeof venueLeadStatsDtoSchema>

/** Every venue's numbers for one pipeline, view and period (docs/gohighlevel-stats.spec.md, "The company table"). */
export const companyLeadStatsDtoSchema = z.object({
  pipelines: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  pipelineId: z.string().uuid().nullable(),
  view: leadStatsViewSchema,
  periods: z.array(z.object({ start: calendarDaySchema, inProgress: z.boolean() })),
  window: adPeriodWindowDtoSchema.nullable(),
  /** The company's numbers and conversions, in order. Conversions are fractions. */
  columns: z.array(z.object({ metricId: z.string().uuid(), name: z.string(), kind: leadStatsMetricKindSchema })),
  rows: z.array(
    z.object({
      venueId: z.string().uuid(),
      venueName: z.string(),
      /** `first_read`: a connection's first read hasn't finished; `not_working`: one has stopped working. */
      state: z.enum(['ready', 'first_read', 'not_working']),
      stillMoving: z.boolean(),
      values: z.array(z.object({ metricId: z.string().uuid(), value: z.number().nonnegative().nullable() })),
    }),
  ),
  /** Couples added up across venues; conversions worked out from those totals. */
  totals: z.array(z.object({ metricId: z.string().uuid(), value: z.number().nonnegative().nullable() })),
})
export type CompanyLeadStatsDto = z.infer<typeof companyLeadStatsDtoSchema>
